import _ from "lodash";
import React from "react";
import classes from "./CaptureImage.scss";
import classNames from "classnames";
import ScreenPanel from "../../ScreenPanel";
import I18nHOC from "UIComponents/I18nHOC";
import { useI18n } from "Hooks";
import FullScreenLoader from "UIComponents/FullScreenLoader";
import { compose } from "lib/apolloClient";
import Webcam from "react-webcam";
import { generateRandomId } from "Utils";
import { CameraIconV2, RotateIcon } from "SvgComponents";
import {
  getCameraDeviceIdList,
  isGlobalHeaderOnMultipleScreensEnabled,
} from "Post/utils";
import {
  setToastMsg,
  enableGlobalHeader,
  disableGlobalHeader,
} from "Login/modules/LoginModule";
import { connect } from "react-redux";
import ACLStore from "lib/aclStore";
import { setGlobalTrayEnabledState } from "Login/modules/LoginModule";
import { hideIntercom, showIntercom } from "modules/Services";
import { updateSettings } from "modules/ConfigurationThunks";
import { getSettingValue } from "modules/PermissionModule";
import { getUserEntityType, getUserId } from "modules/CommonSelectors";

import ScreenHeaderV2 from "../CaptureVideo/ScreenHeaderV2/Header";
import {
  ReloadArrowOutlined,
  ImageMirrorOutlined,
  CameraOutlined,
} from "@toddle-edu/ds-icons";
import { IconButton, Tooltip } from "@toddle-edu/ds-web";
import { getGlobalHeaderWiseScreenRevampStatus } from "Teacher/routes/Courses/routes/Course/modules/CourseModule";

const FACING_MODE_USER = "user";
const FACING_MODE_ENVIRONMENT = "environment";

function dataURLtoBlob(dataUrl) {
  const arr = _.split(dataUrl, ",");
  const mime = _.get(_.split(_.get(arr, "[0]", ""), /:(.*?);/), 1);
  const bstr = atob(_.get(arr, "[1]", ""));
  let n = bstr?.length;
  const u8arr = new Uint8Array(n);

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new Blob([u8arr], { type: mime });
}

const CaptureImageButton = ({ onClick, showDesignV2 }) => {
  const { t } = useI18n(["a11y"]);
  const showCaptureImageButtonV2 = showDesignV2;
  return (
    <button
      className={
        showCaptureImageButtonV2
          ? classes.captureImageButtonV2
          : classes.captureImageButton
      }
      onClick={onClick}
      aria-label={t("a11y:capture_image")}
    >
      <CameraIconV2 />
    </button>
  );
};

const FlipCameraButton = ({ onClick }) => {
  return (
    <div className={classes.flipCameraButton} onClick={onClick}>
      <RotateIcon />
    </div>
  );
};

const FlipCameraButtonV2 = ({ onClick }) => {
  return (
    <IconButton
      icon={<ReloadArrowOutlined />}
      onClick={onClick}
      variant={"outlined-subtle"}
      size={"medium"}
    />
  );
};

const CaptureImageButtonV3 = ({ onClick, t }) => {
  return (
    <Tooltip
      dsVersion="2.0"
      tooltip={t("common:capture_image")}
      placement="top"
    >
      <div className={classes.actionButtons}>
        <IconButton
          dsVersion="2.0"
          icon={<CameraOutlined />}
          onClick={onClick}
          variant={"neutral"}
          size={"large"}
        />
      </div>
    </Tooltip>
  );
};

const FlipCameraButtonV3 = ({ onClick, t }) => {
  return (
    <Tooltip dsVersion="2.0" tooltip={t("common:flip_camera")} placement="top">
      <div className={classes.actionButtons}>
        <IconButton
          dsVersion="2.0"
          icon={<ReloadArrowOutlined />}
          onClick={onClick}
          variant={"neutral"}
          size={"large"}
        />
      </div>
    </Tooltip>
  );
};

const MirrorCameraButton = ({ onClick, t }) => {
  return (
    <Tooltip dsVersion="2.0" tooltip={t("common:mirror_image")} placement="top">
      <div className={classes.actionButtons}>
        <IconButton
          dsVersion="2.0"
          icon={<ImageMirrorOutlined />}
          onClick={onClick}
          variant={"neutral"}
          size={"large"}
        />
      </div>
    </Tooltip>
  );
};

const styles = {
  captureImageContainer: {
    padding: "18px",
  },
  captureImageContainerV2: {
    height: "100vh",
  },
};
const BLANK_OBJECT = {};
class CaptureImage extends React.PureComponent {
  constructor(props) {
    super(props);
    this.webcamRef = React.createRef(null);
    this.state = {
      facingMode: FACING_MODE_USER,
      hasMultipleCameras: false,
      isLoading: true,
      cameraDeviceIds: [],
      currentDeviceId: "",
      isMirrored: props.isMirrored || false,
    };
  }

  componentDidMount() {
    const {
      screenPanelProps: { updatePost },
      setToastMsg,
      setGlobalTrayEnabledState,
      hideIntercom,
      hideGlobalTray = true,
      disableGlobalHeader,
    } = this.props;
    getCameraDeviceIdList()
      .then(devices => {
        const isPresent = _.size(devices) > 1;
        this.setState({
          hasMultipleCameras: isPresent,
          isLoading: false,
          facingMode: isPresent ? FACING_MODE_ENVIRONMENT : FACING_MODE_USER,
          cameraDeviceIds: devices,
          currentDeviceId: _.get(devices, "[0]", ""),
        });
      })
      .catch(e => {
        setToastMsg({
          msg: "toastMsgs:something_went_wrong",
        });
        this.setState({ isLoading: false });
      });
    updatePost({ attachments: [] });
    if (hideGlobalTray) {
      setGlobalTrayEnabledState(false);
    }
    hideIntercom();
    disableGlobalHeader();
  }

  componentWillUnmount() {
    const {
      setGlobalTrayEnabledState,
      showIntercom,
      hideGlobalTray = true,
      enableGlobalHeader,
    } = this.props;
    if (hideGlobalTray) {
      setGlobalTrayEnabledState(true);
    }
    showIntercom();

    isGlobalHeaderOnMultipleScreensEnabled() && enableGlobalHeader();
  }

  handleTakeScreenshotClick = () => {
    const {
      screenPanelProps: {
        updatePost,
        goToStep,
        postDetails: { attachments = [] },
      },
    } = this.props;

    // get base64 encoded string
    const dataUrl = this.webcamRef.current.getScreenshot();
    // generate random id
    const uploadId = generateRandomId();

    // get image resolution
    let imgHeight = 720;
    let imgWidth = 1280;

    if (_.get(this.webcamRef, "current.stream", null)) {
      const webcamSettings = this.webcamRef.current.stream
        ?.getVideoTracks?.()?.[0]
        ?.getSettings?.();
      imgHeight = _.get(webcamSettings, "height", imgHeight);
      imgWidth = _.get(webcamSettings, "width", imgWidth);
    }

    if (dataUrl) {
      // convert data url to blob
      const dataBlob = dataURLtoBlob(dataUrl);
      // create blob url from blob
      const blobUrl = URL.createObjectURL(dataBlob);
      // file name
      const filename = "IMG_".concat(uploadId);
      // create file object
      const file = new File([dataBlob], filename, {
        type: dataBlob.type,
        lastModified: Date.now(),
      });

      const attachment = {
        file: file,
        id: uploadId,
        metadata: { height: imgHeight, size: dataBlob.size, width: imgWidth },
        mimeType: dataBlob.type,
        name: filename,
        type: "IMAGE",
        url: blobUrl,
      };

      updatePost({ attachments: [...attachments, attachment], uploadId });
      if (goToStep) goToStep("POST_DETAIL");
    }
  };

  handleError = e => {
    const { setToastMsg } = this.props;
    const errorMessage = _.get(e, "message", "");
    if (!_.isEmpty(errorMessage)) {
      setToastMsg({
        msg: "toastMsgs:enable_dynamic_permission",
        locale_params: [
          { key: "permission", value: "camera", isPlainText: true },
        ],
      });
    }
    /*if (
      !_.isEmpty(errorMessage) &&
      _.isEqual(errorMessage, "Permission denied")
    ) {
      setToastMsg({
        msg: "toastMsgs:enable_dynamic_permission",
        locale_params: [
          { key: "permission", value: "camera", isPlainText: true },
        ],
      });
    } else {
      setToastMsg({
        msg: "toastMsgs:something_went_wrong",
      });
    }*/
  };

  flipCamera = () => {
    const isCameraDeviceSwitchingEnabled = ACLStore.can(
      "FeatureFlag:CameraDeviceSwitching"
    );

    if (this.state.hasMultipleCameras) {
      const { cameraDeviceIds, currentDeviceId } = this.state;
      const currentIndex = _.findIndex(
        cameraDeviceIds,
        deviceId => deviceId == currentDeviceId
      );
      const numberOfCameras = _.size(cameraDeviceIds);
      if (
        numberOfCameras == 1 &&
        _.get(cameraDeviceIds, "0") == currentDeviceId &&
        currentDeviceId &&
        isCameraDeviceSwitchingEnabled
      ) {
        return;
      }
      const nextIndex =
        currentIndex == -1 || currentIndex == numberOfCameras - 1
          ? 0
          : currentIndex + 1;

      this.setState(({ facingMode }) => ({
        facingMode:
          facingMode === FACING_MODE_USER
            ? FACING_MODE_ENVIRONMENT
            : FACING_MODE_USER,
        currentDeviceId: cameraDeviceIds[nextIndex],
      }));
    }
  };

  toggleMirror = () => {
    const { updateSettings } = this.props;
    this.setState(prevState => {
      const isMirrored = !prevState.isMirrored;
      updateSettings({
        settings: [
          {
            name: "UserImageMirrorSetting",
            value: `${isMirrored}`,
          },
        ],
        showToastMsg: false,
      });
      return {
        isMirrored,
      };
    });
  };

  renderMirrorImageUI = () => {
    const { t } = this.props;
    const { hasMultipleCameras } = this.state;
    return (
      <div className={classes.captureCameraBtnContainer}>
        <MirrorCameraButton t={t} onClick={this.toggleMirror} />
        <CaptureImageButtonV3 t={t} onClick={this.handleTakeScreenshotClick} />
        {hasMultipleCameras && (
          <FlipCameraButtonV3 t={t} onClick={this.flipCamera} />
        )}
      </div>
    );
  };

  render() {
    const { screenPanelProps = {}, t, showDesignV2, title } = this.props;
    const {
      hasMultipleCameras,
      isLoading,
      currentDeviceId,
      facingMode,
      isMirrored,
    } = this.state;
    const isCameraDeviceSwitchingEnabled = ACLStore.can(
      "FeatureFlag:CameraDeviceSwitching"
    );
    const videoConstraints = isCameraDeviceSwitchingEnabled
      ? { deviceId: currentDeviceId }
      : { facingMode: facingMode };

    const { onCloseClick } = screenPanelProps;
    const isGlobalHeaderEnabled = getGlobalHeaderWiseScreenRevampStatus();
    const isMirrorStreamEnabled = ACLStore.can(
      "FeatureFlag:MirrorCameraStream"
    );
    const isMirrorImageEnabled = ACLStore.can("FeatureFlag:MirrorCameraImage");
    const webcamStyle = classNames(classes.webcamScreen, {
      [classes.mirrorStreamView]:
        isMirrorStreamEnabled && !isMirrorImageEnabled,
    });
    return (
      <ScreenPanel
        {...screenPanelProps}
        containerStyle={{ padding: 0 }}
        screenTitle={!showDesignV2 ? t("journal:capture_photo") : ""}
        showScreenTitle={true && !showDesignV2}
        showHeader={!showDesignV2}
      >
        {isLoading && <FullScreenLoader />}
        {showDesignV2 ? (
          <ScreenHeaderV2
            isCaptureImage={true}
            onBackClick={_.isFunction(onCloseClick) && onCloseClick}
            onDoneClick={this.handleTakeScreenshotClick}
            title={title ? title : t("journal:capture_photo")}
            showTitle={true}
            showFlipCamera={hasMultipleCameras && !isMirrorImageEnabled}
            flipCamera={
              hasMultipleCameras && (
                <FlipCameraButtonV2 onClick={this.flipCamera} />
              )
            }
            isGlobalHeaderEnabled={isGlobalHeaderEnabled}
          />
        ) : null}
        <div
          className={classes.captureImageContainer}
          style={showDesignV2 ? styles.captureImageContainerV2 : BLANK_OBJECT}
        >
          <Webcam
            className={webcamStyle}
            ref={this.webcamRef}
            forceScreenshotSourceSize={false}
            imageSmoothing={true}
            mirrored={isMirrorImageEnabled && isMirrored}
            onUserMediaError={this.handleError}
            screenshotFormat={"image/jpeg"}
            screenshotQuality={0.92}
            videoConstraints={videoConstraints}
          />
          {isMirrorImageEnabled ? (
            this.renderMirrorImageUI()
          ) : (
            <div className={classes.captureCameraBtnContainer}>
              <div className={classes.mediaControls}>
                <CaptureImageButton
                  onClick={this.handleTakeScreenshotClick}
                  showDesignV2={showDesignV2}
                />
                <CaptureImageButton
                  onClick={this.handleTakeScreenshotClick}
                  showDesignV2={showDesignV2}
                />
              </div>
              {hasMultipleCameras && !showDesignV2 ? (
                <div className={classes.rightSideCameraOptions}>
                  <FlipCameraButton onClick={this.flipCamera} />
                </div>
              ) : null}
            </div>
          )}
        </div>
      </ScreenPanel>
    );
  }
}

const mapActionCreators = {
  setToastMsg,
  setGlobalTrayEnabledState,
  hideIntercom,
  showIntercom,
  disableGlobalHeader,
  enableGlobalHeader,
  updateSettings,
};

const mapStateToProps = (state, ownProps) => {
  const userId = getUserId();
  const userEntityType = getUserEntityType();

  const isMirrored = getSettingValue({
    name: "UserImageMirrorSetting",
    userId,
    userEntityType,
    readFromUserAndOrgLevel: true,
  });
  return {
    isMirrored,
  };
};
export default compose(
  connect(mapStateToProps, mapActionCreators),
  I18nHOC({ resource: "common" })
)(CaptureImage);
